import React, { useState, useEffect } from 'react';
import { contentApi } from '../../services/api';
import DeleteReasonModal from '../shared/DeleteReasonModal';
import RowActionButtons from '../shared/RowActionButtons';

// List of known universities for autocomplete
const UNIVERSITIES = [
  'Uni Hamburg',
  'TU Hamburg',
  'HafenCity Universität',
  'HAW Hamburg',
  'Bucerius Law School',
  'HfBK Hamburg',
  'Helmut-Schmidt-Universität',
  'Kühne Logistics University',
  'Hochschule für Musik und Theater',
  'Muthesius Kunsthochschule',
  'Christian-Albrechts-Universität Kiel',
  'HAW Kiel',
  'TH Lübeck',
  'Hochschule Flensburg',
  'Musikhochschule Lübeck',
  'HTW Berlin',
  'TU Berlin',
  'LMU Munich',
  'TUM',
  'RWTH Aachen',
  'University of Heidelberg',
  'University of Bonn',
  'University of Munich',
];

function PostList({ onPostSelect }) {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state (no postType filter since we only show regular posts)
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [filters, setFilters] = useState({
    authorName: '',
    enrolledUniversity: '',
    dateFrom: '',
    dateTo: ''
  });
  const [tempFilters, setTempFilters] = useState({ ...filters });

  // Quick filter for reported posts
  const [reportedOnly, setReportedOnly] = useState(false);

  // University autocomplete state
  const [showUniversitySuggestions, setShowUniversitySuggestions] = useState(false);
  const [universitySuggestions, setUniversitySuggestions] = useState([]);

  const perPage = 20;

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const fetchPosts = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = { page, per_page: perPage, post_type: 'post' };
      if (search) params.search = search;
      if (filters.authorName) params.author_name = filters.authorName;
      if (filters.enrolledUniversity) params.enrolled_university = filters.enrolledUniversity;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (reportedOnly) params.reported_only = true;

      const response = await contentApi.listPosts(params);
      setPosts(response.data.posts);
      setTotal(response.data.total);
    } catch (err) {
      setError('Fehler beim Laden der Beiträge');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, filters, reportedOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPosts();
  };

  // Issue #408: Delete confirmation with documented reason
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const openDeleteModal = (postId) => setDeleteTargetId(postId);
  const closeDeleteModal = () => setDeleteTargetId(null);

  const handleDeleteConfirm = async (reason) => {
    await contentApi.deletePost(deleteTargetId, reason);
    setDeleteTargetId(null);
    fetchPosts();
  };

  // Issue #418: Auto-deleted posts (5+ reports) expose Wiederherstellen
  // instead of Löschen, so admins are not misled into deleting content
  // that is already gone from the system.
  const [restoreTargetId, setRestoreTargetId] = useState(null);
  const openRestoreModal = (postId) => setRestoreTargetId(postId);
  const closeRestoreModal = () => setRestoreTargetId(null);
  const handleRestoreConfirm = async (reason) => {
    await contentApi.restorePost(restoreTargetId, reason);
    setRestoreTargetId(null);
    fetchPosts();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleOpenFilterPopover = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    setShowFilterPopover(false);
  };

  const handleCancelFilters = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(false);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      authorName: '',
      enrolledUniversity: '',
      dateFrom: '',
      dateTo: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setReportedOnly(false);
    setPage(1);
  };

  const handleToggleReportedOnly = () => {
    setReportedOnly(!reportedOnly);
    setPage(1);
  };

  // Handle university input change with autocomplete
  const handleUniversityInputChange = (value) => {
    setTempFilters({ ...tempFilters, enrolledUniversity: value });
    if (value.length > 0) {
      const filtered = UNIVERSITIES.filter(uni =>
        uni.toLowerCase().includes(value.toLowerCase())
      );
      setUniversitySuggestions(filtered);
      setShowUniversitySuggestions(filtered.length > 0);
    } else {
      setUniversitySuggestions([]);
      setShowUniversitySuggestions(false);
    }
  };

  const handleUniversitySelect = (university) => {
    setTempFilters({ ...tempFilters, enrolledUniversity: university });
    setShowUniversitySuggestions(false);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Beiträge</h1>
        <p className="page-subtitle">{total} Beiträge insgesamt</p>
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Inhalt suchen..."
            className="search-input"
          />
          <button type="submit" className="search-button">Suchen</button>
        </form>

        <button
          type="button"
          className={`quick-filter-button ${reportedOnly ? 'active' : ''}`}
          onClick={handleToggleReportedOnly}
        >
          Gemeldete Beiträge
        </button>

        <div className="filter-controls">
          <button
            type="button"
            className="filter-button"
            onClick={handleOpenFilterPopover}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Filter
            {activeFilterCount > 0 && (
              <span className="filter-count-badge">{activeFilterCount}</span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="reset-filter-button"
              onClick={handleResetFilters}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Filter Popover */}
      {showFilterPopover && (
        <div className="filter-popover-overlay" onClick={handleCancelFilters}>
          <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
            <div className="filter-popover-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <span className="filter-title">Filter</span>
            </div>

            <div className="filter-popover-content">
              {/* Author Name */}
              <div className="filter-section">
                <label className="filter-section-label">Name des Autors</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Vor- oder Nachname eingeben..."
                  value={tempFilters.authorName}
                  onChange={(e) => setTempFilters({ ...tempFilters, authorName: e.target.value })}
                />
              </div>

              {/* Enrolled University */}
              <div className="filter-section">
                <label className="filter-section-label">Hochschule des Autors</label>
                <div className="autocomplete-container">
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="z.B. TU Hamburg, HTW Berlin..."
                    value={tempFilters.enrolledUniversity}
                    onChange={(e) => handleUniversityInputChange(e.target.value)}
                    onFocus={() => {
                      if (tempFilters.enrolledUniversity.length > 0) {
                        const filtered = UNIVERSITIES.filter(uni =>
                          uni.toLowerCase().includes(tempFilters.enrolledUniversity.toLowerCase())
                        );
                        setUniversitySuggestions(filtered);
                        setShowUniversitySuggestions(filtered.length > 0);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow click on suggestion
                      setTimeout(() => setShowUniversitySuggestions(false), 200);
                    }}
                  />
                  {showUniversitySuggestions && universitySuggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {universitySuggestions.map((uni, index) => (
                        <li
                          key={index}
                          className="autocomplete-suggestion"
                          onClick={() => handleUniversitySelect(uni)}
                        >
                          {uni}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Date Range */}
              <div className="filter-section">
                <label className="filter-section-label">Zeitraum</label>
                <div className="date-range-inputs">
                  <div className="date-input-group">
                    <label className="date-label">Von</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.dateFrom}
                      onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="date-input-group">
                    <label className="date-label">Bis</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.dateTo}
                      onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="filter-popover-buttons">
                <button
                  type="button"
                  className="filter-cancel-button"
                  onClick={handleCancelFilters}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="filter-apply-button"
                  onClick={handleApplyFilters}
                >
                  Anwenden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading-container"><div className="loading-spinner"></div></div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Autor</th>
                  <th>Hochschule</th>
                  <th>Anonym</th>
                  <th>Inhalt</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {posts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-table-message">
                      {activeFilterCount > 0 ? 'Keine Beiträge gefunden mit den gewählten Filtern' : 'Keine Beiträge vorhanden'}
                    </td>
                  </tr>
                ) : (
                  posts.map((post) => (
                    <tr
                      key={post.id}
                      className={`clickable-row ${(post.report_count || 0) >= 5 ? 'reported-row' : ''}`}
                      onClick={() => onPostSelect && onPostSelect(post.id)}
                      style={{ cursor: onPostSelect ? 'pointer' : 'default' }}
                    >
                      <td>
                        {post.is_anonymous ? <em>Anonym</em> : post.author_name}
                        <br /><small>{post.author_email}</small>
                      </td>
                      <td>{post.author_university || '-'}</td>
                      <td>{post.is_anonymous && <span className="anonym-checkmark">✓</span>}</td>
                      <td className="content-cell">
                        {post.content.substring(0, 100)}
                        {post.content.length > 100 && '...'}
                      </td>
                      <td>{formatDate(post.created_at)}</td>
                      <td>
                        <RowActionButtons
                          onView={(e) => {
                            e.stopPropagation();
                            onPostSelect && onPostSelect(post.id);
                          }}
                          {...(post.deleted_at
                            ? {
                                onRestore: (e) => {
                                  e.stopPropagation();
                                  openRestoreModal(post.id);
                                },
                              }
                            : {
                                onDelete: (e) => {
                                  e.stopPropagation();
                                  openDeleteModal(post.id);
                                },
                              })}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="pagination-btn">← Zurück</button>
              <span className="pagination-info">Seite {page} von {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="pagination-btn">Weiter →</button>
            </div>
          )}
        </>
      )}

      {/* Issue #408: Delete-with-reason modal */}
      <DeleteReasonModal
        open={deleteTargetId !== null}
        title="Beitrag löschen"
        description="Sie sind dabei, diesen Beitrag zu löschen. Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDeleteConfirm}
        onCancel={closeDeleteModal}
      />

      {/* Issue #418: Restore-with-reason modal (reuses the documented-reason
          modal so the audit trail stays consistent with delete and lock). */}
      <DeleteReasonModal
        open={restoreTargetId !== null}
        title="Beitrag wiederherstellen"
        description="Dieser Beitrag wurde aufgrund mehrfacher Meldungen automatisch gelöscht. Bitte geben Sie einen Grund für die Wiederherstellung an."
        prompt="Bitte geben Sie einen Grund für die Wiederherstellung an (mindestens 20 Zeichen):"
        placeholder="Grund für die Wiederherstellung eingeben..."
        confirmLabel="Wiederherstellen"
        failureFallback="Wiederherstellen fehlgeschlagen"
        onConfirm={handleRestoreConfirm}
        onCancel={closeRestoreModal}
      />
    </div>
  );
}

export default PostList;
